<template>
  <div
    :style="{ display: inlineBlock ? 'inline-block' : 'inherit',
      'width': !tooltipMode ? '100%' : 'auto' }">
    <!-- Tooltip mode -->
    <template v-if="tooltipMode">
      <div
        :style="{
          display: inlineBlock ? 'inline-block' : 'inherit', ...customStyleSlot,
        }"
        role="presentation"
        @mouseenter="showPopper()"
        @mouseleave="hidePopper()">
        <slot></slot>
      </div>
      <div v-if="!disable && !hasError">
        <div
          v-show="popperShow && !swal2Displayed"
          :style="{ display: displayStyle }"
          :id="popperIdLocal"
          class="popper-component"
          :class="{ 'popper-warning': warning }">
          <button
            v-if="closeButton"
            @click="hidePopper()"
            type="button"
            class="js-popper-close popper-close">
            <slot name="close-button">{{ closeButton }}</slot>
          </button>
          <div v-show="popperShow && !swal2Displayed">
            <slot name="popover"></slot>
          </div>
          <div
            v-show="popperShow && !swal2Displayed"
            class="popper__arrow"
            :style="popperArrowClass"
            x-arrow>
          </div>
        </div>
      </div>
    </template>
    <!-- Custom mode: caller has to modify display/hiding of popper by a
      passed variable -->
    <template v-else>
      <div
        style="width: 100%;"
        :style="{ display: inlineBlock ? 'inline-block' : 'inherit' }">
        <slot></slot>
      </div>
      <div
        v-if="customAttribs.show && !swal2Displayed && !disable && !hasError"
        style="width: 100%;"
        :id="popperIdLocal">
        <button
          v-if="closeButton"
          @click="hidePopper()"
          type="button"
          class="js-popper-close popper-close">
          <slot name="close-button">{{ closeButton }}</slot>
        </button>
        <div
          :style="{ display: displayStyle }"
          style="position: absolute;"
          v-show="customAttribs.show && !swal2Displayed">
          <slot name="popover">
          </slot>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// Originally forked from:
// https://github.com/antongorodezkiy/vue-popper-component
import _ from 'lodash';

import { createPopper } from '@popperjs/core';
import { detect } from 'detect-browser';

import { Mutex } from 'async-mutex';

import { TweakColorBrightness, WarningColor, colorStyle }
  from 'assets/js/colors';

export default {
  name: 'q-popper',

  async beforeUnmount() {
    const lockRelease = await this.mutex.acquire();
    document.body.removeEventListener('scroll', this.handleScroll);
    clearInterval(this.swalListener);

    if (this.popper) {
      this.popper.destroy();
    }

    // Hack: Sometimes popper will dangle if there is a route change, so if this
    // happens just remove them from the DOM rendering by setting display: none.
    for (let i = 0; i < this.MAX_REMOVAL_ATTEMPTS; i += 1) {
      const popId = this.popoverID || this.popperIdLocal;
      const pageElement = document.getElementById(popId);
      if (pageElement) {
        pageElement.style.display = 'none';
        break;
      }
      await this.sleep(this.REMOVAL_DELAY);
    }
    lockRelease();
  },

  computed: {
    // bgColorComputed gets around the inability to set default values
    // on props using computed properties like theme.
    bgColorComputed() {
      return this.bgColor || colorStyle(this.theme).color;
    },

    displayStyle() {
      if (this.tooltipMode && !this.popperShow) return 'none';
      if (!this.tooltipMode && !this.customAttribs.show) return 'none';
      return this.inlineBlock ? 'inline-block' : 'inherit';
    },

    popperIdLocal() {
      return `popper-${this.uniqueId}`;
    },

    theme() {
      return this.$store.state.theme;
    },
  },

  created() {
    document.body.addEventListener('scroll', this.handleScroll);

    // Mutation observers would be the preferred method here, but because
    // of IE compatibility issues we use a poll.
    this.swalListener = setInterval(() => {
      this.swal2Displayed = document.body.classList.contains('swal2-shown');
    }, 250);

    this.uniqueId = this.uuid4();
  },

  data() {
    return {
      MAX_REMOVAL_ATTEMPTS: 10,
      REMOVAL_DELAY: 50,

      anyMouseOver: false,
      hasError: false,
      mutex: new Mutex(),
      nodeSensitiveBrowsers: ['edge', 'ie'],
      pageElement: null,
      pageElementInstance: null,
      popper: null,
      popperShow: false,
      slotEle: null,
      swal2Displayed: false,
      swalListener: null,
      tableElement: null,
      uniqueId: null,
    };
  },

  methods: {
    async handleScroll() {
      if (this.pageElementInstance !== null) {
        await this.popper.update();
      }
    },

    async initPopper() {
      if (this.disable) return;
      const lockRelease = await this.mutex.acquire();
      const slotElement = document.getElementById(this.hostEleID);
      if (!slotElement) {
        console.warn('can\'t find element given by ' +
          `id ${this.hostEleID} for hostEleID`);
        this.hasError = true;
      }
      this.slotEle = slotElement;
      let pageElement = document.getElementById(this.popperIdLocal);

      // We can override the internal popover ID with an external one if
      // popper.js is having trouble properly determining the bounds of the
      // slot content.
      if (this.popoverID) {
        pageElement = document.getElementById(this.popoverID);

        if (!pageElement) {
          console.warn('failed to find page element', this.popoverID);
        }
      }

      this.tableElement = null;
      if (this.boundingEleID) {
        this.tableElement = document.getElementById(this.boundingEleID);
      } else {
        this.tableElement = document.getElementById('main-container');
      }

      try {
        let bgColor = this.bgColorComputed;
        if (this.warning) bgColor = WarningColor;
        pageElement.style.setProperty('--custom-color', bgColor);
        let fgColor = TweakColorBrightness(bgColor, 220);
        if (this.invertedColors) {
          fgColor = TweakColorBrightness(bgColor, -210);
        }
        pageElement.style.setProperty('--custom-foreground-color', fgColor);
      } catch (err) {
        console.warn(`Unable to set popper style attributes: ${err} ` +
          `(slotElement: ${this.hostEleID}, pageElement: ` +
          `${this.popoverID ? this.popoverID : this.popperIdLocal}, ` +
          `boundingElement: ${this.boundingEleID})`);
        this.hasError = true;
      }

      // Inject the popover content into the body of the hosting UI element.
      if (slotElement && this.tableElement && pageElement) {
        // Edge only allows you to append if you first remove the node
        // from its current parent.
        const browser = detect();
        if (_.includes(this.nodeSensitiveBrowsers, _.get(browser, 'name'))) {
          const removeChild = _.get(pageElement, 'parent.removeChild');
          if (_.isFunction(removeChild)) removeChild(pageElement);
        }
        this.tableElement.appendChild(pageElement);
      }

      let createResolver;
      const createPrms = new Promise((resolve) => {
        createResolver = resolve;
      });
      try {
        this.popper = createPopper(
          slotElement, // Thing tooltip goes on
          pageElement, // Tooltip
          {

            modifiers: [
              {
                name: 'flip',
                options: {
                  flipVariations: false,
                  fallBackPlacements: ['left', 'bottom', 'top'],
                },
              },
              {
                name: 'offset',
                options: {
                  offset: this.offset,
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  boundary: this.tableElement ||
                    document.getElementById('main-container'),
                },
              },
            ],
            onFirstUpdate: () => {
              createResolver();
            },
            placement: this.placement || 'bottom',
            removeOnDestroy: false,
            strategy: 'absolute',
          },
        );
        if (pageElement) this.pageElement = pageElement;
      } catch (err) {
        console.warn(`unable to load popper: ${err}`);
        this.hasError = true;
        // Popper is finicky, we should mostly ignore these errors.
        // this.$emit('emitError', err);
      }
      await createPrms;

      if (this.popperLoaded) this.popperLoaded();

      if (this.extraAlign) {
        await this.sleep(0);
        await this.popper.update();
      }
      lockRelease();
    },

    hidePopper() {
      this.popperShow = false;
    },

    showPopper() {
      this.popperShow = true;
    },

    async teardown() {
      if (!this.popper) return;
      const lockRelease = await this.mutex.acquire();
      if (!this.tooltipMode &&
        this.pageElement &&
        this.pageElement.parentNode &&
        this.popper) {
        this.pageElement.parentNode.removeChild(this.pageElement);
      }

      this.popper.destroy();
      lockRelease();
    },
  },

  async mounted() {
    await this.$nextTick();
    if (this.popperShow || (!this.tooltipMode && this.customAttribs.show)) {
      await this.initPopper();
    }
    if (this.tooltipMode) {
      await this.initPopper();
    }
  },

  props: {
    // Arrays
    offset: {
      required: false,
      default: () => [0, 5],
    },

    // Booleans
    disable: {
      type: Boolean,
      required: false,
      default: false,
    },
    extraAlign: {
      type: Boolean,
      required: false,
      default: false,
    },
    inlineBlock: {
      type: Boolean,
      required: false,
      default: true,
    },
    invertedColors: {
      type: Boolean,
      required: false,
      default: false,
    },
    mobileView: {
      type: Boolean,
      required: false,
      default: false,
    },
    tooltipMode: {
      type: Boolean,
      required: false,
      default: true,
    },
    warning: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Function
    popperLoaded: {
      type: Function,
      required: false,
    },

    // Objects

    // customAttribs is to be used in conjunction with tooltipMode being
    // disabled. It is not required, but the component will never display
    // anything without it being passed.
    customAttribs: {
      type: Object,
      required: false,
      default: () => ({ show: false }),
    },
    customStyleSlot: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    popperArrowClass: {
      required: false,
      default: false,
    },

    // Strings
    bgColor: {
      type: String,
      required: false,
      default: null,
    },
    boundingEleID: {
      type: String,
      required: false,
      default: '',
    },
    closeButton: {
      type: String,
      required: false,
      default: null,
    },
    hostEleID: {
      type: String,
      required: false,
      default: '',
    },
    placement: {
      type: String,
      required: false,
      default: 'top',
    },
    popoverID: {
      type: String,
      required: false,
      default: '',
    },
  },

  watch: {
    'customAttribs.show': { // val, oldVal
      handler: async function handle(val) {
        if (val) {
          await this.$nextTick();
          await this.initPopper();
        } else if (!this.tooltipMode) {
          await this.teardown();
        }
      },
    },

    disable: {
      handler: function handle(val) {
        if (this.pageElement && val) {
          this.pageElement.style.display = 'none';
        }
      },
    },

    popperShow: { // val, oldVal
      handler: async function handle(val) {
        if (val) {
          await this.$nextTick();
          await this.initPopper();
        } else if (!this.tooltipMode) {
          await this.teardown();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~assets/css/colors.scss';

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  $popper-background-color: var(--custom-color);
  $popper-foreground-color: var(--custom-foreground-color);

  .popper-component {
    background: #1e394c;
    color: white;
    background: $popper-background-color;
    color: $popper-foreground-color;
    padding: 10px;
    -webkit-border-radius: 3px;
    -moz-border-radius: 3px;
    border-radius: 3px;
    z-index: 1100;
    -webkit-box-shadow: 1px 1px 0px rgba(0, 0, 0, 0.1);
    -moz-box-shadow: 1px 1px 0px rgba(0, 0, 0, 0.1);
    box-shadow: 1px 1px 0px rgba(0, 0, 0, 0.1);

    .popper-close {
      position: absolute;
      top: 2px;
      right: 4px;
      color: black;
      background: transparent;
      border: none;

      &:active,
      &:focus {
        outline: none;
      }
    }

    .popper__arrow {
      width: 0;
      height: 0;
      border-style: solid;
      position: absolute;
      margin: 5px;
    }

    &[data-popper-placement^="top"] {
      padding-bottom: 5px;

      .popper__arrow {
        border-width: 5px 5px 0 5px;
        border-color: $popper-background-color transparent transparent
          transparent;
        bottom: -5px;
        left: calc(50% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &[data-popper-placement^="top-end"] {
      padding-bottom: 5px;

      .popper__arrow {
        border-width: 5px 5px 0 5px;
        border-color: $popper-background-color transparent transparent
          transparent;
        bottom: -5px;
        left: calc(50% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &[data-popper-placement^="bottom"] {
      padding-top: 5px;

      .popper__arrow {
        border-width: 0 5px 5px 5px;
        border-color: transparent transparent $popper-background-color
          transparent;
        top: -5px;
        left: calc(50% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &[data-popper-placement^="right"] {
      .popper__arrow {
        border-width: 5px 5px 5px 0;
        border-color: transparent $popper-background-color transparent
          transparent;
        left: -5px;
        top: calc(50% - 5px);
        margin-left: 0;
        margin-right: 0;
      }
    }

    &[data-popper-placement^="left"] {
      padding-right: 5px;

      .popper__arrow {
        border-width: 5px 0 5px 5px;
        border-color: transparent transparent transparent
          $popper-background-color;
        right: -5px;
        top: calc(50% - 5px);
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
</style>
